import { IDropdownOption } from '@fluentui/react';
import { Field, Label, Input, FluentProvider, webLightTheme } from '@fluentui/react-components';
import React, { useState } from 'react';
import { Controller, FieldValues, UseFormMethods } from 'react-hook-form';
import { IAddress, IAddressCharacterLimits } from '../../../utils/types/IAddress';
import ICountry from '../../../utils/types/ICountry';
import ITag from '../../../utils/types/ITag';
import { DoubleRequiredMarker, RequiredMarker } from '../../common/formFields/RequiredMarker';
import CountryPicker from '../../countries/CountryPicker';
import StateProvinceRegionDropDown from './StateProvinceRegionDropDown';

interface IAddressFields {
  form: UseFormMethods<FieldValues>;
  data: IAddress;
  fieldName: string;
  disabled?: boolean;
  onlyRequireCountry?: boolean;
  displayDoubleRequiredMarker: boolean;
  characterLimits: IAddressCharacterLimits;
}

const AddressFields = ({
  form,
  data,
  fieldName,
  disabled,
  displayDoubleRequiredMarker,
  onlyRequireCountry,
  characterLimits,
}: IAddressFields): JSX.Element => {
  const { control, errors, setValue, trigger, formState } = form;
  const initialSelectedCountry = data?.country
    ? [{ key: data?.country?.id, text: data?.country?.shortName, name: data?.country?.shortName }]
    : null;
  const [selectedCountry, setSelectedCountry] = useState<ITag[]>(initialSelectedCountry);

  const onCountryUpdate = (items: ITag[]): void => {
    const countries = items.map((item) => {
      const newObject: ICountry = {
        id: item.key.toString(),
        shortName: item.name,
      };
      return newObject;
    });
    setSelectedCountry(items);
    setValue(`${fieldName}.country`, countries.length ? countries[0] : null);
    setValue(`${fieldName}.stateProvinceRegion`, null);
    if (formState.isSubmitted) {
      trigger();
    }
  };

  const onStateProvinceRegionUpdate = (
    event: React.ChangeEvent<HTMLSelectElement>,
    option?: IDropdownOption,
    index?: number,
  ): void => {
    setValue(`${fieldName}.stateProvinceRegion`, { id: option?.key, name: option?.text });
  };

  const renderRequiredMarkers = (): JSX.Element => {
    return displayDoubleRequiredMarker ? <DoubleRequiredMarker /> : <RequiredMarker />;
  };

  const customLabelStyles = { paddingBottom: 0 };

  return (
    <FluentProvider theme={webLightTheme}>
      <div role="form">
        <Controller
          name={`${fieldName}.country`}
          control={control}
          defaultValue={initialSelectedCountry}
          required
          rules={{
            required: {
              value: onlyRequireCountry,
              message: 'Please enter a country',
            },
          }}
          render={(): React.ReactElement => (
            <CountryPicker
              itemLimit={1}
              label="Country"
              selectedItems={selectedCountry}
              setSelectedItems={onCountryUpdate}
              isDisabled={disabled}
              errorMessage={errors[fieldName]?.country?.message}
            />
          )}
        />
        <Controller
          name={`${fieldName}.stateProvinceRegion`}
          control={control}
          defaultValue={data?.stateProvinceRegion}
          render={(): React.ReactElement => (
            <StateProvinceRegionDropDown
              selectedCountry={selectedCountry}
              defaultValue={data?.stateProvinceRegion?.id}
              onChange={onStateProvinceRegionUpdate}
              label={
                <Label style={customLabelStyles}>
                  State/Province/Region
                  {onlyRequireCountry ? null : renderRequiredMarkers()}
                </Label>
              }
              disabled={disabled}
            />
          )}
        />
        <Controller
          control={control}
          name={`${fieldName}.streetNumber`}
          defaultValue={data?.streetNumber || ''}
          rules={{
            maxLength: {
              value: characterLimits.streetNumber,
              message: `Street Number can only be a max of ${characterLimits.streetNumber} characters`,
            },
          }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field
              label={
                <Label style={customLabelStyles}>
                  Street Number {onlyRequireCountry ? null : renderRequiredMarkers()}
                </Label>
              }
              validationMessage={errors[fieldName]?.streetNumber?.message}
            >
              <Input
                id={`${fieldName}.streetNumber`}
                disabled={disabled}
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                onChange={(ev, inputData) => onChange(inputData.value)}
              />
            </Field>
          )}
        />
        <Controller
          control={control}
          name={`${fieldName}.streetNumber2`}
          defaultValue={data?.streetNumber2 || ''}
          rules={{
            maxLength: {
              value: characterLimits.streetNumber2,
              message: `Street Number 2 can only be a max of ${characterLimits.streetNumber2} characters`,
            },
          }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field
              label="Street Number 2"
              validationMessage={errors[fieldName]?.streetNumber2?.message}
            >
              <Input
                id={`${fieldName}.streetNumber2`}
                disabled={disabled}
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                onChange={(ev, inputData) => onChange(inputData.value)}
              />
            </Field>
          )}
        />
        <Controller
          control={control}
          name={`${fieldName}.city`}
          defaultValue={data?.city || ''}
          rules={{
            maxLength: {
              value: characterLimits.city,
              message: `City can only be a max of ${characterLimits.city} characters`,
            },
          }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field
              label={
                <Label style={customLabelStyles}>
                  City {onlyRequireCountry ? null : renderRequiredMarkers()}
                </Label>
              }
              validationMessage={errors[fieldName]?.city?.message}
            >
              <Input
                id={`${fieldName}.city`}
                disabled={disabled}
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                onChange={(ev, inputData) => onChange(inputData.value)}
              />
            </Field>
          )}
        />
        <Controller
          control={control}
          name={`${fieldName}.postalZipCode`}
          defaultValue={data?.postalZipCode || ''}
          rules={{
            maxLength: {
              value: characterLimits.postalZipCode,
              message: `Postal Zip Code can only be a max of ${characterLimits.postalZipCode} characters`,
            },
          }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field
              label={
                <Label style={customLabelStyles}>
                  Postal Code {onlyRequireCountry ? null : renderRequiredMarkers()}
                </Label>
              }
              validationMessage={errors[fieldName]?.postalZipCode?.message}
            >
              <Input
                id={`${fieldName}.postalZipCode`}
                disabled={disabled}
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                onChange={(ev, inputData) => onChange(inputData.value)}
              />
            </Field>
          )}
        />
      </div>
    </FluentProvider>
  );
};

export default AddressFields;
